'use client';

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './header';
import Footer from './footer';
import whiteRoad from './Assets/whiteRoad.png';

export default function Pricing() {
  const [frequency, setFrequency] = useState('monthly');
  const [hoveredTier, setHoveredTier] = useState(null);

  const tiers = [
    {
      name: 'Starter',
      id: 'tier-starter',
      href: '#',
      price: { monthly: '$210', annually: '$2,268' },
      costPerUser: { monthly: '$42', annually: '$38' },
      description: 'Essential tools for small paving teams.',
      features: ['Up to 5 team members', '3k Measurements per month', 'Access to Projects', 'Access to Integrations'],
      featured: false,
      cta: 'Start Now',
      color: 'from-stone-700 to-stone-800'
    },
    {
      name: 'Pro',
      id: 'tier-pro',
      href: '#',
      price: { monthly: '$675', annually: '$7,290' },
      costPerUser: { monthly: '$27', annually: '$24' },
      description: 'Designed for growing paving companies.',
      features: ['Up to 25 team members', '12k Measurements per month', 'Access to Projects', 'Access to Integrations'],
      featured: true,
      cta: 'Start Now',
      color: 'from-yellow-600 to-yellow-800'
    },
    {
      name: 'Master',
      id: 'tier-master',
      href: '#',
      price: { monthly: '$1,800', annually: '$19,440' },
      costPerUser: { monthly: '$24', annually: '$21' },
      description: 'Built for established paving firms with larger teams.',
      features: ['Up to 75 team members', '40k Measurements per month', 'Access to Projects', 'Access to Integrations'],
      featured: false,
      cta: 'Start Now',
      color: 'from-stone-700 to-stone-800'
    },
    {
      name: 'Elite',
      id: 'tier-elite',
      href: '#',
      price: { monthly: '$2,875', annually: '$31,050' },
      costPerUser: { monthly: '$23', annually: '$21' },
      description: 'Crafted for large-scale paving companies.',
      features: ['Up to 125 team members', '100k Measurements', 'Access to Projects', 'Access to Integrations'],
      featured: false,
      cta: 'Start Now',
      color: 'from-stone-700 to-stone-800'
    },
  ];

  const faqs = [
    {
      question: 'What does "Measurements per month" mean?',
      answer: 'Measurements refer to individual measurements uploaded to TransTech Connect. A month represents one month of service, and unused measurements do not roll over to the next month.',
    },
    {
      question: 'Can I change plans later?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.',
    },
    {
      question: 'How does team member access work?',
      answer: 'Each team member gets their own login credentials with customizable permissions. Team members can be added or removed at any time up to your plan\'s limit.',
    },
    {
      question: 'Do you offer custom enterprise plans?',
      answer: 'Yes, we offer custom enterprise solutions for organizations with specific needs. Contact our sales team to discuss your requirements.',
    },
  ];

  return (
    <div className="bg-black text-white min-h-screen font-sans">
      <Header />
      
      <main className="isolate">
        {/* Hero Section */}
        <div className="relative pt-14">
          <div className="py-12 relative">
            <img src={whiteRoad} alt="Background" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
            
            <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
              <div className="mx-auto max-w-2xl text-center">
                <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl mb-6">
                  Transparent Pricing
                </h1>
                <p className="mx-auto mt-6 max-w-xl text-lg text-gray-300">
                  Choose the plan that's right for your team and scale as you grow.
                </p>
                
                {/* Billing toggle */}
<div className="mt-8 flex flex-col items-center">
  <div className="relative flex rounded-full p-1 bg-stone-800 w-fit mb-6">
    <button
      type="button"
      className={`relative rounded-full py-2 px-6 text-sm font-medium whitespace-nowrap focus:outline-none ${
        frequency === 'monthly'
          ? 'bg-yellow-500 text-black shadow'
          : 'text-gray-300 hover:text-white'
      }`}
      onClick={() => setFrequency('monthly')}
    >
      Monthly
    </button>
    <button
      type="button"
      className={`relative ml-0.5 rounded-full py-2 px-6 text-sm font-medium whitespace-nowrap focus:outline-none ${
        frequency === 'annually'
          ? 'bg-yellow-500 text-black shadow'
          : 'text-gray-300 hover:text-white'
      }`}
      onClick={() => setFrequency('annually')}
    >
      Annually
      <span className="absolute -top-3 -right-2 rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold text-black">
        Save 10%
      </span>
    </button>
  </div>
  
  {/* Try for Free button */}
  <a 
    href="/signup" 
    className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-medium py-3 px-8 rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-yellow-500/10 animate-pulse-slow"
  >
    Try for Free
  </a>
</div>
              </div>
            </div>
          </div>

          
        </div>



        {/* Pricing Section */}
        <section className="py-12">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
              {tiers.map((tier) => (
                <div
                  key={tier.id}
                  className={`relative flex flex-col rounded-2xl bg-gradient-to-br ${tier.color} border border-stone-700 p-6 shadow-xl transition-all duration-300 ${
                    hoveredTier === tier.id ? 'scale-105 shadow-yellow-500/20' : ''
                  } ${tier.featured ? 'ring-2 ring-yellow-500' : ''}`}
                  onMouseEnter={() => setHoveredTier(tier.id)}
                  onMouseLeave={() => setHoveredTier(null)}
                >
                  {tier.featured && (
                    <div className="absolute -top-3.5 left-0 right-0 mx-auto w-32 rounded-full bg-yellow-500 px-3 py-1 text-center text-xs font-semibold text-black">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                    <p className="mt-2 text-sm text-gray-300">{tier.description}</p>
                  </div>
                  
                  <div className="mb-8">
                    <p className="flex items-baseline">
                      <span className="text-4xl font-bold tracking-tight text-white">{tier.price[frequency]}</span>
                      <span className="ml-2 text-sm text-gray-400">/{frequency === 'monthly' ? 'month' : 'year'}</span>
                    </p>
                    <p className="mt-1 text-sm text-gray-400">
                      About {tier.costPerUser[frequency]} per user/month
                    </p>
                  </div>
                  
                  <ul className="mb-8 space-y-3 text-sm text-gray-300">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center">
                        <svg className="h-5 w-5 flex-shrink-0 text-yellow-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                        </svg>
                        <span className="ml-3">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* <div className="mt-auto pt-4">
                    <a
                      href={tier.href}
                      className={`block w-full rounded-lg py-3 px-4 text-center text-sm font-semibold shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                        tier.featured
                          ? 'bg-yellow-500 text-black hover:bg-yellow-400 focus-visible:outline-yellow-500'
                          : 'bg-stone-800 text-white hover:bg-stone-700 focus-visible:outline-stone-900'
                      }`}
                    >
                      {tier.cta}
                    </a>
                  </div> */}
                </div>
              ))}
            </div>
          </div>
               {/* Custom Plan Box */}
<div className="mx-auto max-w-7xl px-6 lg:px-8 mt-12">
  <div className="rounded-2xl bg-gradient-to-br from-stone-800 to-stone-900 border border-stone-700 p-8 shadow-xl">
    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
      <div>
        <h3 className="text-2xl font-bold text-white">Need a custom plan?</h3>
        <p className="mt-2 text-gray-300">
          Our team can work with you to create a tailored solution that perfectly fits your organization's needs.
        </p>
      </div>
      <a
        href="https://transtechsys.com/products/salesRequest"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-yellow-500 text-black font-medium py-3 px-8 rounded-lg hover:bg-yellow-400 transition-colors duration-300 whitespace-nowrap flex-shrink-0"
      >
        Contact Sales
      </a>
    </div>
  </div>
</div>
        
        </section>

   
        {/* Comparison Table */}
        <section className="py-12 sm:py-20 bg-stone-900">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Compare Plan Features</h2>
              <p className="mt-4 text-lg text-gray-300">
                Get a detailed breakdown of what's included in each plan.
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-stone-700">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-white sm:pl-0">
                      Feature
                    </th>
                    {tiers.map((tier) => (
                      <th 
                        key={tier.id} 
                        scope="col" 
                        className={`px-3 py-3.5 text-center text-sm font-semibold ${
                          tier.featured ? 'text-yellow-500' : 'text-white'
                        }`}
                      >
                        {tier.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-800">
                  {[
                    { feature: 'Team Members', values: ['Up to 5', 'Up to 25', 'Up to 75', 'Up to 125'] },
                    { feature: 'Measurements per Month', values: ['3,000', '12,000', '40,000', '100,000'] },
                    { feature: 'Projects', values: ['✓', '✓', '✓', '✓'] },
                    { feature: 'Integrations', values: ['✓', '✓', '✓', '✓'] },
                  ].map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td className="py-4 pl-4 pr-3 text-sm font-medium text-white sm:pl-0">
                        {row.feature}
                      </td>
                      {row.values.map((value, idx) => (
                        <td 
                          key={idx} 
                          className={`px-3 py-4 text-center text-sm ${
                            tiers[idx].featured ? 'text-yellow-500' : 'text-gray-300'
                          }`}
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-12 sm:py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Frequently Asked Questions</h2>
              <p className="mt-4 text-lg text-gray-300">
                Have a different question? Contact our support team.
              </p>
            </div>
            
            <div className="mx-auto max-w-3xl">
              <dl className="divide-y divide-stone-800">
                {faqs.map((faq, index) => (
                  <div key={index} className="py-6 md:py-8">
                    <dt className="text-lg font-semibold text-white">{faq.question}</dt>
                    <dd className="mt-3 text-base text-gray-300">{faq.answer}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-12 sm:py-16 relative">
          <div className="absolute inset-0 -z-10">
            <img src={whiteRoad} alt="Background" className="h-full w-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70"></div>
          </div>
          
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to Get Started?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg text-gray-300">
                Sign up for a free trial today or schedule a personalized demo to see how TransTech Connect can work for your specific needs.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-x-6">
                <a
                  href="#"
                  className="bg-yellow-500 text-black font-medium py-3 px-8 rounded-lg hover:bg-yellow-400 transition-colors duration-300 w-full sm:w-auto text-center"
                >
                  Start for Free
                </a>
                <a
                  href="https://transtechsys.com/products/salesRequest"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border border-yellow-500 text-yellow-500 font-medium py-3 px-8 rounded-lg hover:bg-yellow-500 hover:text-black transition-colors duration-300 w-full sm:w-auto text-center"
                >
                  Schedule a Demo
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}